import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

import CustomAlert from '@common/components/Feedback/CustomAlert';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { CopyIcon } from '@common/utils/icons/icons';
import DialogWrapperWithClose from '@common/components/General/DialogWrapperWithClose';

import ExternalApi, {
  ApiKeyScope,
  API_KEY_SCOPES,
  MANUFACTURER_ONLY_SCOPES,
  PostApiKey,
} from '../../../models/organization/external-api';

interface CreateApiKeyDialogProps {
  open: boolean;
  organizationId: string;
  isManufacturer: boolean;
  /** Non-null switches the dialog into its one-time reveal state. */
  createdKey: ExternalApi | null;
  errorMessage: string | null;
  isSubmitting: boolean;
  onCreate: (payload: PostApiKey) => void;
  onClose: () => void;
}

const CreateApiKeyDialog: React.FC<CreateApiKeyDialogProps> = ({
  open,
  organizationId,
  isManufacturer,
  createdKey,
  errorMessage,
  isSubmitting,
  onCreate,
  onClose,
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [hasCopied, setHasCopied] = useState(false);

  const availableScopes = API_KEY_SCOPES.filter(
    (scope) => isManufacturer || !MANUFACTURER_ONLY_SCOPES.includes(scope),
  );

  const toggleScope = (scope: ApiKeyScope) => {
    setScopes((prevState) =>
      prevState.includes(scope) ? prevState.filter((s) => s !== scope) : [...prevState, scope],
    );
  };

  const submitHandler = () => {
    setHasCopied(false);
    onCreate({ organizationId, name: name.trim(), scopes });
  };

  const closeHandler = () => {
    setName('');
    setScopes([]);
    setHasCopied(false);
    onClose();
  };

  const copySecretHandler = () => {
    if (createdKey?.apiKey != null) {
      setHasCopied(true);
      navigator.clipboard.writeText(createdKey.apiKey);
    }
  };

  const secret = createdKey?.apiKey ?? null;

  return (
    <DialogWrapperWithClose
      open={open}
      onClose={closeHandler}
      fullWidth
      maxWidth="sm"
      title={secret != null ? t('title-api-key-created') : t('title-create-api-key')}
    >
      <DialogContent>
        {secret != null ? (
          <Stack spacing={2} pt={1}>
            <CustomAlert
              severity="warning"
              header={t('warning-api-key-shown-once')}
              data-testid="api-key-shown-once-warning"
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Typography sx={{ wordBreak: 'break-all' }}>{secret}</Typography>
              <Stack direction="row" spacing={1} justifyContent="center">
                <TooltipIconButton
                  icon={
                    <CopyIcon
                      data-testid="copy-secret-button"
                      color={hasCopied ? 'success' : undefined}
                    />
                  }
                  onClick={copySecretHandler}
                  tooltip={t('info-tooltip-copy-to-clipboard')}
                  tooltipProps={{ placement: 'left' }}
                />
                {hasCopied && <Typography>{t('label-copied')}</Typography>}
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={2} pt={1}>
            {errorMessage != null && <CustomAlert severity="error" header={errorMessage} />}

            <TextField
              data-testid="api-key-name-input"
              label={t('input-label-api-key-name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
            />

            <Stack>
              <Typography variant="subtitle2">{t('input-label-api-key-scopes')}</Typography>
              <Typography variant="caption">{t('form-help-text-api-key-scopes')}</Typography>
              {availableScopes.map((scope) => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      checked={scopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                      inputProps={
                        {
                          'data-testid': `api-key-scope-${scope}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  }
                  label={t(`label-api-key-scope-${scope.replace(':', '-')}`)}
                />
              ))}
            </Stack>

            <Typography variant="caption">{t('form-help-text-api-key-expiry')}</Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {secret != null ? (
          <Button variant="contained" onClick={closeHandler}>
            {t('button-close')}
          </Button>
        ) : (
          <React.Fragment>
            <Button onClick={closeHandler}>{t('button-cancel')}</Button>
            <Button
              data-testid="create-api-key-submit"
              variant="contained"
              disabled={name.trim().length === 0 || isSubmitting}
              onClick={submitHandler}
            >
              {t('button-create')}
            </Button>
          </React.Fragment>
        )}
      </DialogActions>
    </DialogWrapperWithClose>
  );
};

export default CreateApiKeyDialog;
